/**
 * Emergency answers queue.
 *
 * Every answer is written locally BEFORE being sent (write-ahead), and removed once the server
 * acknowledges it: what is left in the store is exactly what the server never received. There is
 * therefore no outage to detect, and an API down, a database down, a network cut or a closed tab
 * are all covered by the same mechanism.
 *
 * Ordering is arbitrated by a monotonic `sequence` per applicant rather than by dates, which
 * settles the race between a replay still in flight and a newer save going through the normal
 * path: see acknowledgeAnswer below and the monotonicity guard in the API.
 */

import moment from 'moment';

import { getAnchoredNow } from '../../util/timeAnchor';
import { uploadFile } from '../../util/file';
import { sequence } from '../../util/promises';
import {
    isOfflineStorageAvailable,
    requestPersistentStorage,
    initOfflineKeys,
    signAnswer,
    putAnswerEntry,
    markAnswerEntryFailed,
    getAnswerEntry,
    getAnswerEntries,
    removeAnswerEntry,
    putBlobEntry,
    getBlobEntry,
    removeBlobEntry,
    purgeExpiredEntries,
    clearApplicantData,
    putSnapshot,
    getSnapshot,
} from '../../util/offlineStorage';

// Delay between two replay attempts, doubling on each failure
export const RETRY_DELAY_MIN_IN_MS = 5000;
export const RETRY_DELAY_MAX_IN_MS = 60000;

let isAvailable = false;
let answerSequence = 0;
let previousHash = '';
let expiresAt = null;
let lastSuccessDate = null;
let retryDelay = RETRY_DELAY_MIN_IN_MS;
let retryTimeout = null;

// One replay at a time per question: a drain in flight must not race with itself
const questionLocks = {};

// The banner must react to any save, wherever it was triggered from (a single question or the
// whole page), so the queue tells its watchers instead of being polled at a few chosen places
const queueListeners = new Set();

export function subscribeToQueueChange(listener) {
    queueListeners.add(listener);
    return () => queueListeners.delete(listener);
}

function notifyQueueChange(applicantId) {
    return getPendingAnswers(applicantId).then(entries => {
        // The questions are passed along so each one can drop its own "saved on this computer"
        // badge as soon as its replay goes through
        const pendingQuestionsIds = entries.map(entry => entry.question);
        queueListeners.forEach(listener => listener(entries.length, pendingQuestionsIds));
        return entries.length;
    });
}

/*
 * Enable the queue for an exam session. Returns false when the device cannot secure answers
 * locally (private browsing, storage disabled, insecure context): the caller then warns the
 * applicant and keeps the previous behaviour.
 */
export function initOfflineAnswers(applicantId, secret, windowEnd) {
    expiresAt = windowEnd ? moment.utc(windowEnd).format() : null;
    return isOfflineStorageAvailable().then(available => {
        if(!available) {
            isAvailable = false;
            return false;
        }
        requestPersistentStorage();
        return initOfflineKeys(secret).then(hasKeys => {
            isAvailable = hasKeys;
            if(!isAvailable) {
                return false;
            }
            // A reloaded page restarts from the highest sequence already stored: starting back at
            // zero would make every new save look older than the queue, and the API would discard
            // them as outdated replays
            return getAnswerEntries(applicantId).then(entries => {
                answerSequence = (entries || []).reduce((highest, entry) => Math.max(highest, entry.sequence || 0), answerSequence);
                // Anything already in the store outlived a reload or a crash, so it never reached
                // the service whatever its state said before
                return markAnswersFailed(applicantId, (entries || []).filter(entry => !entry.hasFailed).map(entry => entry.question)).then(() => true);
            });
        });
    }).catch(() => {
        isAvailable = false;
        return false;
    });
}

export function isOfflineAnswersEnabled() {
    return isAvailable;
}

/*
 * Stop securing answers when leaving the exam. The queue itself is left untouched: it may still
 * hold answers to replay, and the sequence must keep growing when the applicant comes back.
 */
export function disableOfflineAnswers() {
    isAvailable = false;
    cancelScheduledDrain();
}

export function getLastSuccessDate() {
    return lastSuccessDate;
}

/*
 * Build the offline block joined to every save. `attempts` starts at 1: the API only traces the
 * answer on the copy when it really went through the queue (a failed attempt, or a deferred save).
 */
function buildOfflineData() {
    answerSequence += 1;
    return {
        savedAt: getAnchoredNow().format(),
        sequence: answerSequence,
        previousHash,
        attempts: 1,
    };
}

/*
 * A replay is an answer sent back from the local queue, still carrying the signature stored with
 * it. An answer returned by the API carries an offline block too, with the verification status
 * ('valid', 'invalid'...) in place of the signature: editing it again is a new save, which must be
 * dated, sequenced and signed again. Taking it for a replay sent the status as a signature, and
 * every later edit of a replayed answer was flagged as altered.
 */
function isQueuedReplay(answer) {
    const signature = (answer.offline || {}).signature;
    if(!signature) {
        return Promise.resolve(false);
    }
    return getAnswerEntry(answer.applicant, answer.question).then(entry => !!entry && ((entry.answer || {}).offline || {}).signature === signature);
}

/*
 * Write an answer locally and return it enriched with its signed offline block, ready to be sent
 */
export function queueAnswer(answer) {
    if(!isAvailable) {
        return Promise.resolve(answer);
    }

    return isQueuedReplay(answer).then(isReplay => {
        // A replay keeps the identity given when it was composed: same date, same sequence, same
        // signature. Re-dating it would move the answer after the pages the applicant went through
        // since, and the previous page filter would then discard it.
        if(isReplay) {
            const replayedAnswer = { ...answer, offline: { ...answer.offline, attempts: (answer.offline.attempts || 1) + 1 } };
            return putAnswerEntry(replayedAnswer, expiresAt, true).then(() => replayedAnswer).catch(err => {
                console.error('OfflineAnswers::: queueAnswer: replay', err);
                return replayedAnswer;
            });
        }

        // Binary answers whose upload failed are stored aside and re-uploaded before the answer itself
        const offline = { ...buildOfflineData(), blobs: (answer.offline || {}).blobs || [] };
        const answerToQueue = { ...answer, offline };

        return signAnswer(answerToQueue).then(signature => {
            const signedAnswer = { ...answerToQueue, offline: { ...offline, signature } };
            previousHash = signature;
            return putAnswerEntry(signedAnswer, expiresAt).then(() => signedAnswer);
        }).catch(err => {
            console.error('OfflineAnswers::: queueAnswer', err);
            return answer;
        });
    });
}

/*
 * Drop the local copy of an acknowledged answer.
 *
 * The entry is removed only when it has not been rewritten since: if the applicant saved the
 * same question again while the acknowledgement was travelling, the stored sequence is higher
 * and the newer answer must stay in the queue.
 */
export function acknowledgeAnswer(applicantId, questionId, acknowledgedSequence) {
    if(!isAvailable) {
        return Promise.resolve(false);
    }
    lastSuccessDate = moment.utc().format();
    return getAnswerEntry(applicantId, questionId).then(entry => {
        if(!entry) {
            return false;
        }
        if(acknowledgedSequence && (entry.sequence || 0) > acknowledgedSequence) {
            // A more recent save is waiting: keep it
            return false;
        }
        return Promise.all([
            removeAnswerEntry(applicantId, questionId),
            ...((entry.blobs || []).map(path => removeBlobEntry(path))),
        ]).then(() => notifyQueueChange(applicantId)).then(() => true);
    }).catch(err => {
        console.error('OfflineAnswers::: acknowledgeAnswer', err);
        return false;
    });
}

/*
 * Report the answers a save never delivered. This is what makes the banner appear, whichever save
 * failed: a single question, the whole page, or an automatic save.
 */
export function markAnswersFailed(applicantId, questionIds = []) {
    if(!isAvailable || !questionIds.length) {
        return Promise.resolve(0);
    }
    return Promise.all(questionIds.map(questionId => markAnswerEntryFailed(applicantId, questionId)))
        .then(() => notifyQueueChange(applicantId))
        .catch(err => {
            console.error('OfflineAnswers::: markAnswersFailed', err);
            return 0;
        });
}

/*
 * Store a binary answer (file or audio question) whose upload failed, so the replay can upload
 * it before sending the answer
 *
 * @returns Promise resolving { isStored, reason } - reason is 'size' or 'quota' when refused
 */
export function queueAnswerBlob(applicantId, path, blob) {
    if(!isAvailable) {
        return Promise.resolve({ isStored: false, reason: 'unavailable' });
    }
    return putBlobEntry(path, blob, applicantId, expiresAt);
}

/*
 * Answers waiting to be replayed: only those a save failed to deliver.
 *
 * An answer being written here is not a problem in itself, it is what the write-ahead does for
 * every single save. Counting those would raise the banner on every successful auto-save.
 */
export function getPendingAnswers(applicantId) {
    if(!isAvailable) {
        return Promise.resolve([]);
    }
    return getAnswerEntries(applicantId).then(entries => entries.filter(entry => entry.hasFailed));
}

export function getPendingCount(applicantId) {
    return getPendingAnswers(applicantId).then(entries => entries.length);
}

/*
 * Upload the binary answers attached to an entry, then send the answer itself
 */
function replayEntry(dispatch, applicantId, entry, sendAnswer) {
    return getAnswerEntry(applicantId, entry.question).then(storedEntry => {
        if(!storedEntry || !storedEntry.answer) {
            return false;
        }

        const uploadBlobs = (entry.blobs || []).reduce((chain, path) => chain.then(isOk => {
            if(!isOk) {
                return false;
            }
            return getBlobEntry(path).then(blobEntry => {
                if(!blobEntry) {
                    // Nothing to upload anymore: the answer can go through
                    return true;
                }
                return uploadFile(path, blobEntry.blob, blobEntry.type).then(result => !!result.isOk).catch(() => false);
            });
        }), Promise.resolve(true));

        return uploadBlobs.then(isUploaded => {
            if(!isUploaded) {
                return false;
            }
            // The attempt counter is bumped by queueAnswer when the answer goes back through the
            // save path, so the API can tell a replay from a save that went through straight away
            return sendAnswer(storedEntry.answer);
        });
    });
}

/*
 * Replay every pending answer, oldest save first
 *
 * @param sendAnswer Function sending one answer to the API, resolving true on success
 * @returns Promise resolving the number of answers still pending
 */
export function drainQueue(dispatch, applicantId, sendAnswer) {
    if(!isAvailable) {
        return Promise.resolve(0);
    }

    return getPendingAnswers(applicantId).then(entries => sequence(entries, entry => {
        if(questionLocks[entry.question]) {
            return Promise.resolve(null);
        }
        questionLocks[entry.question] = true;
        return replayEntry(dispatch, applicantId, entry, sendAnswer)
            .catch(err => {
                console.error('OfflineAnswers::: drainQueue: replay', err);
                return false;
            })
            .then(result => {
                delete questionLocks[entry.question];
                return result;
            });
    })).then(() => notifyQueueChange(applicantId)).catch(err => {
        console.error('OfflineAnswers::: drainQueue', err);
        return 0;
    });
}

/*
 * Schedule the next replay attempt, backing off while the service stays down
 */
export function scheduleDrain(dispatch, applicantId, sendAnswer, onDrained = null) {
    if(!isAvailable || retryTimeout) {
        return;
    }
    retryTimeout = setTimeout(() => {
        retryTimeout = null;
        drainQueue(dispatch, applicantId, sendAnswer).then(pendingCount => {
            if(pendingCount > 0) {
                retryDelay = Math.min(retryDelay * 2, RETRY_DELAY_MAX_IN_MS);
                scheduleDrain(dispatch, applicantId, sendAnswer, onDrained);
            } else {
                retryDelay = RETRY_DELAY_MIN_IN_MS;
            }
            onDrained && onDrained(pendingCount);
        });
    }, retryDelay);
}

export function isDrainScheduled() {
    return !!retryTimeout;
}

export function cancelScheduledDrain() {
    retryTimeout && clearTimeout(retryTimeout);
    retryTimeout = null;
    retryDelay = RETRY_DELAY_MIN_IN_MS;
}

/** Session snapshot, so the exam can start again while the API is still down */

export function saveOfflineSnapshot(applicantId, data) {
    if(!isAvailable) {
        return Promise.resolve(null);
    }
    return putSnapshot(applicantId, data, expiresAt);
}

export function loadOfflineSnapshot(applicantId) {
    return getSnapshot(applicantId);
}

/** Cleanup */

export function purgeOfflineData() {
    return purgeExpiredEntries();
}

/*
 * Called once the exam is validated: nothing is left to send, nothing has to stay on the device
 */
export function clearOfflineAnswers(applicantId) {
    cancelScheduledDrain();
    return clearApplicantData(applicantId);
}

/*
 * Test purpose only
 */
export function resetOfflineAnswersState() {
    isAvailable = false;
    answerSequence = 0;
    previousHash = '';
    expiresAt = null;
    lastSuccessDate = null;
    cancelScheduledDrain();
    Object.keys(questionLocks).forEach(key => delete questionLocks[key]);
}
